import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "../components/ui/dialog";
import Layout from "../components/Layout";
import VideoPlayerDialog from "../components/VideoPlayerDialog";
import { livestreamAPI } from "../services/api";
import {
	ArrowLeft,
	Play,
	Download,
	Clock,
	Calendar,
	FileVideo,
	AlertCircle,
	Trash2,
} from "lucide-react";

interface Recording {
	recording_id: string;
	status: "starting" | "active" | "stopping" | "completed" | "failed";
	type: "room" | "track";
	started_at: string;
	ended_at?: string;
	file_name?: string;
	file_size?: number;
	duration?: number;
	url: string;
}

interface Livestream {
	id: string;
	title: string;
	status: string;
	created_at: string;
}

const LivestreamRecordings: React.FC = () => {
	const { livestreamId } = useParams<{ livestreamId: string }>();
	const navigate = useNavigate();
	// const { addToast } = useToast();
	const addToast = (toast: any) => {
		console.log("Toast:", toast);
		// TODO: Replace with proper toast implementation
	};

	const [recordings, setRecordings] = useState<Recording[]>([]);
	const [livestream, setLivestream] = useState<Livestream | null>(null);
	const [loading, setLoading] = useState(true);
	const [videoPlayerOpen, setVideoPlayerOpen] = useState(false);
	const [selectedRecording, setSelectedRecording] = useState<Recording | null>(
		null,
	);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [recordingToDelete, setRecordingToDelete] = useState<Recording | null>(
		null,
	);
	const [isDeleting, setIsDeleting] = useState(false);

	useEffect(() => {
		if (livestreamId) {
			loadRecordings();
			loadLivestreamInfo();
		}
	}, [livestreamId]);

	const loadRecordings = async () => {
		try {
			setLoading(true);
			const response = await livestreamAPI.getRecordings(livestreamId!);
			setRecordings(response.data.data || []);
		} catch (error: any) {
			console.error("Failed to load recordings:", error);
			addToast({
				variant: "destructive",
				title: "Error",
				description:
					error.response?.data?.message || "Failed to load recordings",
			});
		} finally {
			setLoading(false);
		}
	};

	const loadLivestreamInfo = async () => {
		try {
			const response = await livestreamAPI.get(livestreamId!);
			setLivestream(response.data.data);
		} catch (error: any) {
			console.error("Failed to load livestream info:", error);
		}
	};

	const formatDuration = (seconds?: number): string => {
		if (!seconds) return "Unknown";
		const minutes = Math.floor(seconds / 60);
		const remainingSeconds = seconds % 60;
		return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
	};

	const formatFileSize = (bytes?: number): string => {
		if (!bytes) return "Unknown";
		const mb = bytes / (1024 * 1024);
		if (mb < 1024) {
			return `${mb.toFixed(1)} MB`;
		}
		const gb = mb / 1024;
		return `${gb.toFixed(2)} GB`;
	};

	const formatDate = (dateString: string): string => {
		return new Date(dateString).toLocaleString();
	};

	const getStatusBadge = (status: Recording["status"]) => {
		const statusConfig = {
			starting: { label: "Starting", variant: "secondary" as const },
			active: { label: "Recording", variant: "default" as const },
			stopping: { label: "Stopping", variant: "secondary" as const },
			completed: { label: "Completed", variant: "success" as const },
			failed: { label: "Failed", variant: "destructive" as const },
		};

		const config = statusConfig[status];
		return (
			<Badge variant={config.variant} className="text-xs">
				{config.label}
			</Badge>
		);
	};

	const handleDownload = async (recording: Recording) => {
		if (!recording.url) {
			addToast({
				variant: "destructive",
				title: "Error",
				description: "Recording file not available",
			});
			return;
		}

		try {
			const link = document.createElement("a");
			link.href = recording.url;
			link.download = `livestream_recording_${recording.recording_id}.mp4`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);

			addToast({
				title: "Download Started",
				description: "Recording download has started.",
			});
		} catch (error: any) {
			console.error("Download failed:", error);
			addToast({
				title: "Download Failed",
				description:
					error.response?.data?.message || "Failed to download recording",
			});
		}
	};

	const handlePlayback = (recording: Recording) => {
		if (recording.status !== "completed") {
			addToast({
				variant: "destructive",
				title: "Error",
				description: "Recording is not ready for playback",
			});
			return;
		}

		setSelectedRecording(recording);
		setVideoPlayerOpen(true);
	};

	const handleDeleteClick = (recording: Recording) => {
		setRecordingToDelete(recording);
		setDeleteDialogOpen(true);
	};

	const handleDeleteConfirm = async () => {
		if (!recordingToDelete) return;

		try {
			setIsDeleting(true);
			await livestreamAPI.deleteRecording(
				livestreamId!,
				recordingToDelete.recording_id,
			);

			// Remove from local state
			setRecordings((prev) =>
				prev.filter((r) => r.recording_id !== recordingToDelete.recording_id),
			);

			addToast({
				title: "Success",
				description: "Recording deleted successfully",
			});
		} catch (error: any) {
			console.error("Failed to delete recording:", error);
			addToast({
				variant: "destructive",
				title: "Error",
				description:
					error.response?.data?.message || "Failed to delete recording",
			});
		} finally {
			setIsDeleting(false);
			setDeleteDialogOpen(false);
			setRecordingToDelete(null);
		}
	};

	const handleDeleteCancel = () => {
		setDeleteDialogOpen(false);
		setRecordingToDelete(null);
	};

	if (loading) {
		return (
			<div className="container max-w-6xl mx-auto p-6">
				<div className="flex items-center gap-4 mb-6">
					<Button
						variant="ghost"
						size="sm"
						onClick={() => navigate(-1)}
						className="flex items-center gap-2"
					>
						<ArrowLeft className="h-4 w-4" />
						Back
					</Button>
				</div>
				<div className="flex items-center justify-center min-h-[400px]">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
				</div>
			</div>
		);
	}

	return (
		<Layout title="Livestream Recordings">
			<div className="container max-w-6xl mx-auto p-6">
				{/* Header */}
				<div className="flex items-center justify-between mb-6">
					<div className="flex items-center gap-4">
						<Button
							variant="ghost"
							size="sm"
							onClick={() => navigate(-1)}
							className="flex items-center gap-2"
						>
							<ArrowLeft className="h-4 w-4" />
							Back
						</Button>
						{livestream && (
							<div>
								<h1 className="text-2xl font-bold">{livestream.title}</h1>
								<p className="text-muted-foreground">Livestream Recordings</p>
							</div>
						)}
					</div>
					<Button onClick={loadRecordings} variant="outline" size="sm">
						Refresh
					</Button>
				</div>

				{/* Recordings List */}
				{recordings.length === 0 ? (
					<Card>
						<CardContent className="flex flex-col items-center justify-center py-12">
							<FileVideo className="h-12 w-12 text-muted-foreground mb-4" />
							<h3 className="text-lg font-medium mb-2">No recordings found</h3>
							<p className="text-muted-foreground text-center">
								This livestream doesn't have any recordings yet.
							</p>
						</CardContent>
					</Card>
				) : (
					<div className="space-y-4">
						{recordings.map((recording) => (
							<Card key={recording.recording_id} className="overflow-hidden">
								<CardHeader className="pb-3">
									<div className="flex items-center justify-between">
										<div className="flex items-center gap-3">
											<div className="p-2 bg-primary/10 rounded-lg">
												<FileVideo className="h-5 w-5 text-primary" />
											</div>
											<div>
												<CardTitle className="text-base">
													{recording.file_name ||
														`Recording ${recording.recording_id}`}
												</CardTitle>
												<p className="text-sm text-muted-foreground">
													{recording.type === "room"
														? "Full Livestream"
														: "Track Recording"}
												</p>
											</div>
										</div>
										{getStatusBadge(recording.status)}
									</div>
								</CardHeader>

								<CardContent className="pt-0">
									<div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
										<div className="flex items-center gap-2 text-sm">
											<Calendar className="h-4 w-4 text-muted-foreground" />
											<span>Started: {formatDate(recording.started_at)}</span>
										</div>

										{recording.ended_at && (
											<div className="flex items-center gap-2 text-sm">
												<Calendar className="h-4 w-4 text-muted-foreground" />
												<span>Ended: {formatDate(recording.ended_at)}</span>
											</div>
										)}

										{recording.ended_at && (
											<div className="flex items-center gap-2 text-sm">
												<Clock className="h-4 w-4 text-muted-foreground" />
												<span>
													Duration: {formatDuration(recording.duration)}
												</span>
											</div>
										)}

										{recording.file_size && (
											<div className="flex items-center gap-2 text-sm">
												<FileVideo className="h-4 w-4 text-muted-foreground" />
												<span>Size: {formatFileSize(recording.file_size)}</span>
											</div>
										)}
									</div>

									{recording.status === "failed" && (
										<div className="flex items-center gap-2 text-sm text-destructive mb-4">
											<AlertCircle className="h-4 w-4" />
											<span>Recording failed to complete</span>
										</div>
									)}

									<div className="border-t my-4" />

									<div className="flex items-center justify-between">
										<div className="flex items-center gap-2">
											{recording.status === "completed" && recording.url && (
												<Button
													variant="outline"
													size="sm"
													onClick={() => handlePlayback(recording)}
													className="hidden"
												>
													<Play className="h-4 w-4" />
													Play
												</Button>
											)}
											{recording.status === "completed" && recording.url && (
												<Button
													variant="outline"
													size="sm"
													onClick={() => handleDownload(recording)}
													className="flex items-center gap-2"
												>
													<Download className="h-4 w-4" />
													Download
												</Button>
											)}

											<Button
												variant="outline"
												size="sm"
												onClick={() => handleDeleteClick(recording)}
												className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:border-red-300"
											>
												<Trash2 className="h-4 w-4" />
												Delete
											</Button>
										</div>
									</div>
								</CardContent>
							</Card>
						))}
					</div>
				)}

				{/* Video Player Dialog */}
				{selectedRecording && (
					<VideoPlayerDialog
						isOpen={videoPlayerOpen}
						onClose={() => {
							setVideoPlayerOpen(false);
							setSelectedRecording(null);
						}}
						videoUrl={selectedRecording.url}
						title={
							selectedRecording.file_name ||
							`Recording ${selectedRecording.recording_id.slice(0, 8)}`
						}
					/>
				)}

				{/* Delete Confirmation Dialog */}
				<Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
					<DialogContent className="sm:max-w-[450px] max-h-[85vh] overflow-hidden p-0">
						<div className="flex flex-col max-h-[85vh]">
							<div className="px-6 pt-6 pb-6 flex-shrink-0">
								<DialogHeader className="space-y-4">
									<DialogTitle>Delete Recording</DialogTitle>
									<DialogDescription>
										Are you sure you want to delete this recording? This action
										cannot be undone.
									</DialogDescription>
								</DialogHeader>
							</div>
							
							<div className="flex-1 overflow-y-auto px-6 pb-6">
								{recordingToDelete && (
									<div className="bg-muted p-5 rounded-lg">
										<p className="text-sm">
											<strong>Recording:</strong>{" "}
											{recordingToDelete.file_name ||
												`Recording ${recordingToDelete.recording_id.slice(0, 8)}`}
										</p>
									</div>
								)}
							</div>
							
							<div className="px-6 pb-6 pt-5 border-t flex-shrink-0">
								<DialogFooter className="flex-col-reverse sm:flex-row gap-2">
									<Button
										variant="outline"
										onClick={handleDeleteCancel}
										disabled={isDeleting}
										className="w-full sm:w-auto"
									>
										Cancel
									</Button>
									<Button
										variant="destructive"
										onClick={handleDeleteConfirm}
										disabled={isDeleting}
										className="w-full sm:w-auto"
									>
										{isDeleting ? "Deleting..." : "Delete"}
									</Button>
								</DialogFooter>
							</div>
						</div>
					</DialogContent>
				</Dialog>
			</div>
		</Layout>
	);
};

export default LivestreamRecordings;
